import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, TemplateRef } from '@angular/core';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { DfModuleImageModule } from '@df/module-image/df-module-image.module';
import { ratioField } from '@df/module-image/icms/icms-ratio-field';
import { ImageModule } from '@df/module-image/image.module';
import { BodyClassService } from '@df/ui/body-class.service';
import { DfUiModule } from '@df/ui/df-ui.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { TradeStoreService } from '../../../multisite/trade/trade-store.service';
import { NLoginViewLayout } from './login-view-layout.interface';

/**
 * Block data interface
 */
export type ILoginViewLayoutData = Catalog.IBlockData<ILoginViewLayoutContent>;
/**
 * Block content interface
 */
export interface ILoginViewLayoutContent extends NLoginViewLayout.ILoginViewLayoutContent {
    /**
     * Login view layout options
     */
    useAlternativeVariant?: boolean;
    alternativeVariantMessage?: string;
}
export const LOGIN_VIEW_LAYOUT_BODY_CLASS = 'is-login-view-layout';
/**
 * Component definition
 */
@Component({
    selector: 'login-view-layout',
    templateUrl: './login-view-layout.component.html',
    styleUrls: ['./login-view-layout.component.scss'],
    standalone: true,
    imports: [DfUiModule, DfModuleImageModule, ImageModule, NgTemplateOutlet, NgClass],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<LoginViewLayoutComponent>({
    id: 'login-page-layout',
    block: {
        url: 'login-page-layout'
    },
    onDemandOnly: true,
    name: 'Login page layout',
    description: 'Login page layout block',
    fields: [
        {
            type: NICmsContentField.EFieldType.label,
            key: 'label-information',
            title: 'Login background image'
        },
        {
            key: 'image',
            type: NICmsContentField.EFieldType.image,
            title: 'Image'
        },
        {
            key: 'logoTrade',
            type: NICmsContentField.EFieldType.image,
            title: 'Logo - trade'
        },
        {
            key: 'message',
            type: NICmsContentField.EFieldType.textarea,
            title: 'Message'
        },
        {
            key: 'useAlternativeVariant',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Alternative Variant',
            default: false
        },
        {
            key: 'alternativeVariantMessage',
            type: NICmsContentField.EFieldType.textarea,
            title: 'Message for alternative variant',
            canShow: (data: ILoginViewLayoutContent) => data?.useAlternativeVariant
        },
        <NICmsContentField.IField>{
            key: 'sectionTerms',
            type: NICmsContentField.EFieldType.label,
            title: 'Terms and conditions'
        },
        <NICmsContentField.IField>{
            key: 'termsCopy',
            type: NICmsContentField.EFieldType.textarea,
            title: 'Terms copy'
        },
        <NICmsContentField.IField>{
            key: 'termsFile',
            type: NICmsContentField.EFieldType.document,
            title: 'Terms and conditions file'
        },
        {
            key: 'mobileImage',
            type: NICmsContentField.EFieldType.image,
            title: 'Mobile image'
        },
        ratioField({
            key: 'mobileImageRatio',
            title: 'Mobile image ratio',
            default: '16/9'
        })
    ]
})
export class LoginViewLayoutComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: ILoginViewLayoutContent;
    @Input()
    template!: TemplateRef<any>;
    private bodyClassService = inject(BodyClassService);
    private tradestoreService = inject(TradeStoreService);
    override ngOnInit() {
        super.ngOnInit();
        if (this.tradestoreService.isTradeStore()) {
            this.bodyClassService.add(LOGIN_VIEW_LAYOUT_BODY_CLASS, this);
        }
    }
    override ngOnDestroy() {
        super.ngOnDestroy();
        this.bodyClassService.remove(LOGIN_VIEW_LAYOUT_BODY_CLASS, this);
    }
}
